import { Chip, Stack, Tooltip } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { styleHelpers } from '@/theme';
import CategoryIcon from '@/components/CategoryIcon';
import type { Category } from '@/types';

interface CategoryFilterChipProps {
    category: Category;
    tooltip?: string;
    isHidden: boolean;
    isLocked?: boolean;
    borderColor?: string;
    borderWidth?: number;
    onToggle: () => void;
}

const CategoryFilterChip = ({
    category,
    tooltip,
    isHidden,
    isLocked,
    borderColor = 'divider',
    borderWidth = 1,
    onToggle,
}: CategoryFilterChipProps) => {
    const chipElement = (
        <Chip
            icon={
                <Stack
                    direction="row"
                    sx={{
                        alignItems: 'center',
                        gap: 0.5,
                        ml: 0.5,
                    }}
                >
                    <CategoryIcon category={category.icon} />
                    {isLocked && <LockIcon sx={{ fontSize: '0.9rem' }} />}
                </Stack>
            }
            label={category.name}
            onClick={isLocked ? undefined : onToggle}
            disabled={isLocked}
            variant={isHidden ? 'outlined' : 'filled'}
            sx={{
                ...styleHelpers.gradientChip(!isHidden),
                height: { xs: 44, sm: 40 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                px: 1.5,
                cursor: isLocked ? 'not-allowed' : 'pointer',
                opacity: isLocked && isHidden ? 0.5 : 1,
                '& .MuiChip-icon': {
                    fontSize: '1.2rem',
                    marginLeft: 0,
                    marginRight: 0,
                    color: isHidden ? 'text.secondary' : 'primary.light',
                },
                '& .MuiChip-label': {
                    px: 1,
                },
                ...(isLocked && {
                    borderColor,
                    borderWidth,
                    '&:hover': {
                        transform: 'none',
                        boxShadow: 'none',
                    },
                }),
            }}
        />
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} arrow>
                <span>{chipElement}</span>
            </Tooltip>
        );
    }

    return chipElement;
};

export default CategoryFilterChip;
